import React from "react";
import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "poverty-inequality-india-measurement-tendulkar-committee",
  title: "Poverty & Inequality in India: Measurement Methods, Committees, Gini Coefficient & Latest Data",
  description: "Learn about poverty and inequality in India: absolute vs relative poverty, Tendulkar and Rangarajan committees, MPI, and how to calculate the Gini coefficient.",
  pubDate: "2025-08-21",
  authorSlug: "dhairya-madaan",
  category: "subject-explainers",
  tags: ["indian economy", "poverty", "inequality", "gini coefficient", "development economics"],
  readMinutes: 16,
  heroEyebrow: "Development Economics"
};

export function Body() {
  return (
    <>
      <p>
        Poverty and inequality remain central themes in India's development discourse. While rapid economic growth since 1991 has pulled millions out of extreme poverty, income and wealth inequality have concurrently widened. This article provides comprehensive university-level notes on the measurement of poverty in India, the evolution of expert committees (like the Tendulkar committee poverty line), the measurement of inequality via the Gini coefficient, and major government anti-poverty programmes.
      </p>

      <h2>1. Absolute vs Relative Poverty</h2>
      <p>
        <strong>Absolute Poverty:</strong> A condition characterized by severe deprivation of basic human needs, including food, safe drinking water, sanitation facilities, health, shelter, education, and information. It is measured against a fixed benchmark (the poverty line).
      </p>
      <p>
        <strong>Relative Poverty:</strong> Defines poverty in relation to the economic status of other members of the society. It essentially measures inequality. A person is poor if their income is significantly lower than the median income of the country.
      </p>

      <h2>2. Evolution of Poverty Measurement in India</h2>
      <p>
        India measures absolute poverty primarily using consumption expenditure data collected by the National Sample Survey Office (NSSO), rather than income data, because consumption is more stable and better reflects actual living standards.
      </p>

      <h3>Key Expert Committees</h3>
      
      <h4>A. Alagh Committee (1979)</h4>
      <p>
        Constructed a poverty line based entirely on nutritional requirements. The minimum calorie intake was set at:
        <ul>
          <li>Rural: 2400 kcal per capita per day</li>
          <li>Urban: 2100 kcal per capita per day</li>
        </ul>
      </p>

      <h4>B. Lakdawala Committee (1993)</h4>
      <p>
        Retained the calorie-based methodology but recommended state-specific poverty lines using the Consumer Price Index for Agricultural Labourers (CPI-AL) for rural areas and the Consumer Price Index for Industrial Workers (CPI-IW) for urban areas.
      </p>

      <h4>C. Tendulkar Committee (2009)</h4>
      <p>
        Radically shifted the methodology by moving away from purely calorie-based anchors.
        <ul>
          <li>Included private expenditure on health and education in the poverty line basket.</li>
          <li>Used a uniform poverty line basket (Urban) for both rural and urban areas, adjusted for price differences.</li>
          <li>Poverty lines defined: Rs 27 per day (Rural) and Rs 33 per day (Urban).</li>
          <li>Estimated India's poverty at 21.9% (for 2011-12).</li>
        </ul>
      </p>

      <h4>D. Rangarajan Committee (2014)</h4>
      <p>
        Reverted partially to the normative calorie requirement but updated the basket.
        <ul>
          <li>Calories: 2155 (Rural) and 2090 (Urban).</li>
          <li>Included normative levels for proteins and fats.</li>
          <li>Poverty lines defined: Rs 32 per day (Rural) and Rs 47 per day (Urban).</li>
          <li>Estimated India's poverty at 29.5% (for 2011-12).</li>
        </ul>
      </p>

      <h3>Committee Comparison Table</h3>
      <table border={1} cellPadding={5} style={{ borderCollapse: 'collapse', width: '100%', marginBottom: '20px' }}>
        <thead>
          <tr>
            <th>Feature</th>
            <th>Lakdawala (1993)</th>
            <th>Tendulkar (2009)</th>
            <th>Rangarajan (2014)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Anchor</td>
            <td>Calorie Intake</td>
            <td>Broader Consumption (Health/Edu)</td>
            <td>Calories + Proteins + Fats + Non-food</td>
          </tr>
          <tr>
            <td>Poverty Line (2011-12)</td>
            <td>-</td>
            <td>Rural: ₹816/mo, Urban: ₹1000/mo</td>
            <td>Rural: ₹972/mo, Urban: ₹1407/mo</td>
          </tr>
          <tr>
            <td>Poverty % (2011-12)</td>
            <td>-</td>
            <td>21.9%</td>
            <td>29.5%</td>
          </tr>
        </tbody>
      </table>

      <h2>3. Modern Measurement: Multidimensional Poverty Index (MPI)</h2>
      <p>
        Moving beyond monetary measures, the UNDP's Global MPI and NITI Aayog's National MPI measure deprivation across three equally weighted dimensions:
      </p>
      <ul>
        <li><strong>Health:</strong> Nutrition, Child & Adolescent Mortality, Maternal Health.</li>
        <li><strong>Education:</strong> Years of schooling, School attendance.</li>
        <li><strong>Standard of Living:</strong> Cooking fuel, sanitation, drinking water, electricity, housing, assets, bank accounts.</li>
      </ul>
      <p>According to NITI Aayog, steep declines in multidimensional poverty have been observed recently due to heavy push in sanitation (Swachh Bharat) and cooking fuel (Ujjwala).</p>

      <h2>4. Measuring Inequality: Lorenz Curve & Gini Coefficient</h2>
      <p>
        <strong>Lorenz Curve:</strong> A graphical representation of income distribution. The cumulative percentage of population is plotted on the x-axis, and the cumulative percentage of income on the y-axis. A perfectly equal society forms a 45-degree line (Line of Equality). The further the Lorenz curve bows away from this line, the higher the inequality.
      </p>
      <p>
        <strong>Gini Coefficient:</strong> A numerical measure of inequality ranging from 0 (perfect equality) to 1 (perfect inequality). It is calculated as the area between the Line of Equality and the Lorenz curve, divided by the total area under the Line of Equality.
        <br/><em>Gini = A / (A + B)</em>
      </p>
      <p>
        <strong>Kuznets Hypothesis:</strong> Proposes an inverted-U relationship between economic growth and inequality. As a poor country grows, inequality first increases (as workers move to higher-paying urban industry), peaks, and then decreases as welfare states emerge and human capital broadens.
      </p>

      <h3>Solved Example: Gini Calculation Approximation</h3>
      <p>
        <strong>Question:</strong> In a simplified economy of 5 people, incomes are 10, 20, 30, 40, and 100. Calculate cumulative shares to plot the Lorenz curve.
      </p>
      <p>
        <strong>Solution:</strong>
        <br />Total Income = 10+20+30+40+100 = 200.
        <br />Population quintiles = 20% each.
        <br />Poorest 20%: Income share = 10/200 = 5%. Cumulative Pop = 20%, Cumul Income = 5%.
        <br />Next 20%: Income share = 20/200 = 10%. Cumul Pop = 40%, Cumul Income = 15%.
        <br />Next 20%: Income share = 30/200 = 15%. Cumul Pop = 60%, Cumul Income = 30%.
        <br />Next 20%: Income share = 40/200 = 20%. Cumul Pop = 80%, Cumul Income = 50%.
        <br />Richest 20%: Income share = 100/200 = 50%. Cumul Pop = 100%, Cumul Income = 100%.
        <br />Plotting these coordinates gives the Lorenz curve. The large jump at the end (richest 20% owning 50%) pulls the Gini coefficient up.
      </p>

      <h2>5. Common Poverty Metrics</h2>
      <ul>
        <li><strong>Headcount Ratio (HCR):</strong> Proportion of population living below the poverty line. (Number of poor / Total population).</li>
        <li><strong>Poverty Gap Ratio (PGR):</strong> Measures the depth of poverty. It is the average shortfall of the total population from the poverty line. PGR = Mean distance below the poverty line (as a proportion of poverty line).</li>
      </ul>

      <h2>6. Anti-Poverty Programmes in India</h2>
      <p>The government attacks poverty through growth-driven and targeted welfare approaches:</p>
      <ul>
        <li><strong>MGNREGA:</strong> Mahatma Gandhi National Rural Employment Guarantee Act guarantees 100 days of wage employment in a financial year to rural households.</li>
        <li><strong>PDS (Public Distribution System):</strong> Ensures food security by distributing subsidized grains. Overhauled by the National Food Security Act (NFSA) 2013.</li>
        <li><strong>PM-KISAN:</strong> Direct income support of ₹6000 per year to farmer families.</li>
        <li><strong>Jan Dhan Yojana:</strong> Financial inclusion program ensuring bank accounts, vital for Direct Benefit Transfer (DBT) to plug leakages.</li>
      </ul>

      <h2>Exam-Style Solved Questions</h2>

      <p><strong>Q1: Why is the Tendulkar methodology considered an improvement over Lakdawala?</strong><br/>
      <em>Ans:</em> Tendulkar moved away from a strict calorie anchor, acknowledging that health and education are essential non-food basic needs. It also unified the poverty basket across rural and urban lines, only adjusting for spatial price differentials, providing a more realistic baseline.</p>

      <p><strong>Q2: If the Headcount Ratio falls but the Poverty Gap Ratio increases, what does this imply?</strong><br/>
      <em>Ans:</em> It implies that while the total number of people below the poverty line has decreased (perhaps those just below the line crossed over), the people who remain in poverty have become even poorer, falling further below the poverty line.</p>

      <p><strong>Q3: Explain the role of DBT in poverty alleviation.</strong><br/>
      <em>Ans:</em> Direct Benefit Transfer bypasses bureaucratic layers, delivering cash subsidies directly to bank accounts (JAM trinity: Jan Dhan, Aadhaar, Mobile). This radically reduces inclusion/exclusion errors and ghost beneficiaries, ensuring state funds actually reach the targeted poor.</p>
    </>
  );
}


